"use client";

import { useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import { Search, Banknote, Users, FileText, TrendingUp } from "lucide-react";
import { PageWrapper, PageHeader, StatCard, Button } from "@/components/ui";
import { BankIcon } from "@/components/bank-icon";
import type { PayerDetail } from "@/lib/mock-data";
import Link from "next/link";

const sectionFadeIn = (delay: number) => ({
  initial: { opacity: 0, y: 10 } as const,
  animate: { opacity: 1, y: 0 } as const,
  transition: { duration: 0.4, ease: [0.23, 1, 0.32, 1] as const, delay },
});

type GroupedPayer = {
  key: string;
  payerName: string;
  payerAccountNumber: string;
  payerBank: string;
  totalVolume: number;
  tradeIds: number[];
  sources: Set<PayerDetail["source"]>;
  records: PayerDetail[];
};

export default function SourceOfFundsPage() {
  const [allDetails, setAllDetails] = useState<PayerDetail[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [bankFilter, setBankFilter] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch("/api/payer-details");
        if (res.ok) {
          const data = await res.json();
          setAllDetails(data.details ?? []);
        }
      } catch {
        // silently fail
      }
      setLoading(false);
    })();
  }, []);

  const grouped = useMemo(() => {
    const map = new Map<string, GroupedPayer>();
    for (const d of allDetails) {
      const key = d.payerAccountNumber || d.payerName.toLowerCase();
      if (!map.has(key)) {
        map.set(key, {
          key,
          payerName: d.payerName,
          payerAccountNumber: d.payerAccountNumber,
          payerBank: d.payerBank,
          totalVolume: 0,
          tradeIds: [],
          sources: new Set(),
          records: [],
        });
      }
      const g = map.get(key)!;
      g.totalVolume += d.amount;
      if (!g.tradeIds.includes(d.tradeId)) g.tradeIds.push(d.tradeId);
      g.sources.add(d.source);
      g.records.push(d);
    }
    return Array.from(map.values());
  }, [allDetails]);

  const filtered = useMemo(() => {
    return grouped.filter((g) => {
      if (bankFilter && g.payerBank.toLowerCase() !== bankFilter.toLowerCase()) return false;
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        if (
          !g.payerName.toLowerCase().includes(q) &&
          !g.payerAccountNumber.includes(q) &&
          !g.payerBank.toLowerCase().includes(q)
        )
          return false;
      }
      return true;
    });
  }, [grouped, searchQuery, bankFilter]);

  const uniquePayers = grouped.length;
  const totalRecords = allDetails.length;
  const totalVolume = allDetails.reduce((s, d) => s + d.amount, 0);
  const ocrCount = allDetails.filter((d) => d.source === "ocr").length;

  const banks = useMemo(() => {
    const set = new Set<string>();
    for (const d of allDetails) {
      if (d.payerBank) set.add(d.payerBank);
    }
    return Array.from(set).sort();
  }, [allDetails]);

  const sourceBadge = (source: PayerDetail["source"]) => {
    const styles: Record<string, string> = {
      ocr: "bg-accent/15 text-accent",
      mono: "bg-blue-500/15 text-blue-400",
      treasury: "bg-blue-500/15 text-blue-400",
      manual: "bg-surface-3 text-muted",
    };
    return styles[source] || styles.manual;
  };

  return (
    <PageWrapper>
      <PageHeader
        title="Source of Funds"
        subtitle="Track and search all payer identities across NGN settlements"
      />

      {/* Stats row */}
      <motion.div {...sectionFadeIn(0.05)} className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <StatCard label="Unique Payers" value={uniquePayers.toLocaleString()} delay={0.05} />
        <StatCard label="Total Records" value={totalRecords.toLocaleString()} delay={0.1} />
        <StatCard label="Total Volume" value={`₦${totalVolume.toLocaleString()}`} delay={0.15} />
        <StatCard label="OCR Extracted" value={ocrCount.toLocaleString()} delay={0.2} />
      </motion.div>

      {/* Search + filter */}
      <motion.div {...sectionFadeIn(0.1)} className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" />
          <input
            type="text"
            placeholder="Search by name, account number, or bank..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-3 py-2 bg-surface rounded-lg text-sm text-foreground placeholder:text-muted/50 focus:outline-none focus:ring-1 focus:ring-accent/30"
            style={{ border: "1px solid var(--border)" }}
          />
        </div>
        <select
          value={bankFilter}
          onChange={(e) => setBankFilter(e.target.value)}
          className="bg-surface rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none"
          style={{ border: "1px solid var(--border)" }}
        >
          <option value="">All Banks</option>
          {banks.map((b) => (
            <option key={b} value={b}>
              {b}
            </option>
          ))}
        </select>
      </motion.div>

      {/* Table */}
      <motion.div {...sectionFadeIn(0.15)} className="bg-surface rounded-xl overflow-hidden" style={{ border: "1px solid var(--border)" }}>
        {loading ? (
          <div className="flex items-center justify-center py-12 text-muted text-sm">Loading...</div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 gap-2">
            <Banknote className="w-8 h-8 text-muted/30" />
            <p className="text-sm text-muted">
              {allDetails.length === 0
                ? "No payer details recorded yet. They will appear here when payments are added with payer information."
                : "No results match your search."}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="text-[10px] text-muted uppercase tracking-wider" style={{ borderBottom: "1px solid var(--border)" }}>
                  <th className="px-4 py-3 font-semibold">Payer Name</th>
                  <th className="px-4 py-3 font-semibold">Account Number</th>
                  <th className="px-4 py-3 font-semibold">Bank</th>
                  <th className="px-4 py-3 font-semibold text-right">Total Volume</th>
                  <th className="px-4 py-3 font-semibold text-center">Trades</th>
                  <th className="px-4 py-3 font-semibold">Source</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((g) => (
                  <tr
                    key={g.key}
                    className="transition-colors hover:bg-surface-2/50"
                    style={{ borderBottom: "1px solid var(--border)" }}
                  >
                    <td className="px-4 py-3">
                      <span className="text-sm font-medium text-foreground">{g.payerName}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-foreground font-mono">
                        {g.payerAccountNumber || <span className="text-muted">—</span>}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-foreground flex items-center gap-1.5">{g.payerBank ? <><BankIcon bankName={g.payerBank} size="sm" />{g.payerBank}</> : <span className="text-muted">—</span>}</span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="text-sm font-semibold text-foreground tabular-nums">
                        ₦{g.totalVolume.toLocaleString()}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center gap-1 flex-wrap">
                        {g.tradeIds.map((tid) => (
                          <Link
                            key={tid}
                            href={`/ledger?trade=${tid}`}
                            className="text-[10px] font-bold text-accent hover:text-accent-hover bg-accent/10 px-1.5 py-0.5 rounded transition-colors"
                          >
                            #{tid}
                          </Link>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 flex-wrap">
                        {Array.from(g.sources).map((src) => (
                          <span key={src} className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${sourceBadge(src)}`}>
                            {src}
                          </span>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </motion.div>
    </PageWrapper>
  );
}
